(ns lupapalvelu.next.modern
  (:require [lupapalvelu.next.ajax :as ajax]
            [lupapalvelu.next.event :refer [<sub >evt]]
            [lupapalvelu.next.session :as sess]
            [lupapalvelu.ui.common :refer [loc]]
            [lupapalvelu.ui.lazy :refer [lazy-component]]
            [lupapalvelu.ui.navbar-connected :refer [connected-navbar]]
            [lupapalvelu.ui.topbar-connected :refer [connected-topbar]]
            [reagent.core :as r :refer [defc]]
            [reagent.dom.client :as rdomc]
            [reagent.hooks :as hooks]))

(defc wrap-check-session
  [view]
  (let [allow-anon?      js/lupapisteApp.allowAnonymous
        user             (<sub [::sess/user])
        in-hashbang-app? (= js/lupapisteApp.startPage "hashbang")]
    (hooks/use-effect (fn []
                        (cond
                          ;; Check if the entrypoint was the hashbang.js
                          ;; in that case we should go the start, instead of
                          ;; displaying current view component.
                          in-hashbang-app?
                          (>evt [:route/replace (keyword js/lupapisteApp.startPage)])

                          (or allow-anon? user) nil

                          (not allow-anon?)
                          (ajax/query "user"
                                      ;; No need for callbacks, when user is available, new wait is hidden
                                      {:success-fn  (partial sess/user-success (fn router-callback-nop []))
                                       :error-fn    sess/error-response
                                       :complete-fn (fn complate-callback-nop [])})

                          :else
                          (>evt [:route/replace :login])))
                      [in-hashbang-app? allow-anon? user])
    (cond
      in-hashbang-app?
      nil

      ;; Show the defined route view component
      (or allow-anon? user)
      view

      ;; Show loading spinner when loading the user session
      (not allow-anon?)
      [:div.ajax-loader-container.block
       [:div
        [:p ""]]]

      :else
      nil)))

(defc topbar-mount
  "Mounts the new topbar and manages the `new-topbar-active` body class
  that hides the legacy nav.nav-wrapper. Only rendered when a user is authenticated."
  []
  (let [user (<sub [::sess/user])]
    (hooks/use-effect
      (fn []
        (when user
          (.add (.-classList (.-body js/document)) "new-topbar-active"))
        (fn []
          (.remove (.-classList (.-body js/document)) "new-topbar-active")))
      [user])
    (when user
      [connected-topbar])))

(defc navbar-mount
  "Mounts the new navbar and manages the `new-navbar-active` body class
  when the navbar is fixed below the topbar on legacy routes."
  []
  (hooks/use-effect
    (fn []
      (.add (.-classList (.-body js/document)) "new-navbar-active")
      (fn []
        (.remove (.-classList (.-body js/document)) "new-navbar-active")))
    [])
  [connected-navbar])

(defc navbar-slot []
  (when (and (<sub [::sess/beta-user?])
             (-> (<sub [:route/data])
                 :navbar))
    [navbar-mount]))

(defc new-footer
  "Reimplements resources/private/footer/footer.html

  By adding footer inside #newApp, we can have it part of flexbox
  layout here."
  []
  (let [current-page (:page (<sub [:route/data]))]
    [:footer.relative
     {:role "contentinfo"}
     [:div.informations
      [:div.footer-box
       [:span.footer-title (loc "footer.conversation.title")]
       [:p
        [:span {:dangerouslySetInnerHTML (r/unsafe-html (loc "footer.conversation.desc"))}]
        ;; Show the "Open discussion" link based on the current page. The same current page information is
        ;; available on the re-frame route match.
        ;; _.includes(sidePanelPages, lupapisteApp.models.rootVMO && lupapisteApp.models.rootVMO.currentPage())
        ;; NOTE: The list of pages names is copied from resources/private/common/app.js
        (when (contains? #{"application" "attachment" "statement" "neighbors" "verdict" "structure"} current-page)
          [:a {:href     "#"
               :on-click (fn [e]
                           (.preventDefault e)
                           (.. js/window -parent -hub (send "side-panel::open")))}
           (loc "footer.open.conversation")])]]
      [:div.footer-box
       [:span.footer-title (loc "footer.report-issue.title")]
       [:p
        [:span (loc "footer.report-issue.desc")]
        [:span [:a {:target "_blank"
                    :href   (loc "footer.report-issue.link-href")}
                (loc "footer.report-issue.link-text")]]]]
      [:div.footer-box
       [:ul.footer-links
        [:li [:a {:href (loc "a11y.url.registry")}
              (loc "footer.register")]]
        [:li [:a {:href "/page/terms/fi"}
              (loc "footer.terms")]]
        [:li [:a {:href "/page/licenses/fi"}
              (loc "footer.license")]]]]
      [:div.footer-box
       [:p " "]]]
     ;; NOTE: On the old view this is always outside of viewport,
     ;; which forces page scroll always!
     [:span.buildinfo
      (when-let [original-el (js/document.getElementById "buildinfo")]
        (.-innerText original-el))]]))

(defc wrap-page
  "Add topbar and footer and setup body styles for new entrypoint views."
  [child]
  (let [topbar?                 (<sub [::sess/beta-user?])
        new-entrypoint-enabled? (boolean child)]
    (hooks/use-effect (fn []
                        (when new-entrypoint-enabled?
                          (.add (.-classList (.-body js/document)) "new-entrypoint")
                          (fn []
                            (.remove (.-classList (.-body js/document)) "new-entrypoint"))))
                      [new-entrypoint-enabled?])
    (when (or child topbar?)
      [:<>
       ;; TODO: separate topbar/navbar logic elsewhere.
       (when topbar?
         [topbar-mount])
       [navbar-slot]
       (when child
         ;; Add flex-1 here to ensure footer is always at the bottom of the page.
         ;; Views can add flex-1 again to control the whole viewport background themselves.
         [:div.flex-1.flex.flex-col
          child])
       (when new-entrypoint-enabled?
         [new-footer])])))

(defn new-root-view
  "React root for routes which use either :view or :loadable property.

  The div where this is mountred, is empty if the current match doesn't have
  either of the properties.

  Old legacy routes use `default-legacy-controller` to hide their own DOM
  elements themselves, so we don't need to care about those.

  Components should use Reagent defc themselves if they want to enable
  fn component implementation for hooks."
  []
  ;; NOTE: Consider route "identity" (React key) so user 1 to user 2
  ;; component would unmount & mount instead of update view?
  ;; Or just handle inside the view-component itself?
  (let [{:keys [loadable view]} (<sub [:route/data])]
    [wrap-page
     (cond
       loadable [wrap-check-session [lazy-component {:loadable loadable}]]
       view     [wrap-check-session [view]]
       :else    nil)]))

(defonce root (rdomc/create-root (js/document.getElementById "newApp")))

(defn ^:dev/after-load init-new-root-view!
  []
  (rdomc/render root [new-root-view]))
